/*
 *
 * The content of this file can be edited freely, but to maintain upgradability
 * this file should not be renamed and should always export an Angular module named
 * `AppDataModule`.
 *
 *
 */
import { InjectionToken, NgModule } from '@angular/core';
import { ACCESS_CONTROL_BASE_PATH } from '@backbase/accesscontrol-v3-http-ang';
import { ACCOUNT_STATEMENT_BASE_PATH } from '@backbase/account-statements-http-ang';
import { ARRANGEMENT_MANAGER_BASE_PATH } from '@backbase/arrangement-manager-http-ang';
import { BILLPAY_BASE_PATH } from '@backbase/billpay-http-ang';
import { CATEGORIES_MANAGEMENT_BASE_PATH } from '@backbase/categories-management-http-ang';
import { IMPERSONATION_SERVICE_BASE_PATH, IMPERSONATION_USER_MANAGER_SERVICE_BASE_PATH } from '@backbase/identity-auth/impersonation';
import { USER_BASE_PATH } from '@backbase/user-http-ang';
import { ENROLLMENT_MANAGER_BASE_PATH } from '@backbase/enrollment-manager-v1-client-ang';
import { environment } from '../environments/environment';
import { INITIATE_PAYMENT_JOURNEY_PAYMENT_ORDER_A2A_BASE_PATH, INITIATE_PAYMENT_JOURNEY_PAYMENT_ORDER_BASE_PATH } from '@backbase/initiate-payment-journey-ang';
import { STOP_CHECKS_JOURNEY_STOP_CHECKS_BASE_PATH } from '@backbase/stop-checks-journey-ang';
import { ACCOUNTS_TRANSACTIONS_JOURNEY_TRANSACTIONS_BASE_PATH } from '@backbase/accounts-transactions-journey-ang';
import { CONTACTS_MANAGER_JOURNEY_CONTACT_MANAGER_BASE_PATH } from '@backbase/contact-manager-journey-ang';
import { SELF_SERVICE_DEVICE_MANAGEMENT_V2_BASE_PATH } from '@backbase/identity-self-service-journey-ang';
import { CONSENT_JOURNEY_CONSENT_BASE_PATH } from '@backbase/consent-journey-ang';
import { CAMPAIGN_JOURNEY_ENGAGEMENT_BASE_PATH } from '@backbase/campaign-space-ang';
// import { AUTHORIZED_USER_JOURNEY_BASE_PATH } from '@backbase/authorized-users-journey-ang';
import { RETAIL_LOANS_JOURNEY_LOANS_BASE_PATH } from '@backbase/loans-retail-journey';
import { PLACES_JOURNEY_BASE_PATH } from '@backbase/places-journey-ang';
import { MANAGE_POCKETS_JOURNEY_FINANCIAL_INSTITUTION_MANAGER_BASE_PATH, MANAGE_POCKETS_JOURNEY_POCKET_TAILOR_BASE_PATH } from '@backbase/manage-pockets-journey-ang';
import { CARDS_MANAGEMENT_JOURNEY_CARDS_BASE_PATH } from '@backbase/cards-management-journey-ang';
import { FI_BUDGET_JOURNEY_BUDGETING_BASE_PATH } from '@backbase/fi-budget-journey-ang';
import { REAL_TIME_COMMUNICATION_JOURNEY_BASE_PATH } from '@backbase/real-time-communication-journey-ang';
import { PASSWORD_RESET_ACCOUNT_RECOVERY_MANAGER_BASE_PATH } from '@backbase/identity-password-reset-journey';

/**
 * Service paths for the individual data modules.
 *
 * The values provided here are mapped to FactoryProviders in the AppDataModules
 * module below, using the servicePathFactory function above to create the
 * factory for each injection token.
 *
 * If for some reason you do not want to use the servicePathFactory to provide
 * the base path for a service, remove it from this array and add a separate
 * provider for it to the AppDataModules module, below.
 *
 * The entries in this array may be edited, added or removed as required, but
 * deleting or renaming the array itself may prevent future upgrades being
 * correctly applied.
 */
const dataModulePaths: [InjectionToken<string>, string][] = [
  [ACCESS_CONTROL_BASE_PATH, '/access-control'],
  [ACCOUNT_STATEMENT_BASE_PATH, '/account-statement'],
  // [ACTIONS_BASE_PATH, '/action'],
  [ARRANGEMENT_MANAGER_BASE_PATH, '/arrangement-manager'],
  // [AUTHORIZED_USER_JOURNEY_BASE_PATH, '/authorized-user'],
  [BILLPAY_BASE_PATH, '/billpay-integrator'],
  [FI_BUDGET_JOURNEY_BUDGETING_BASE_PATH, '/budget-planner'],
  [CARDS_MANAGEMENT_JOURNEY_CARDS_BASE_PATH, '/cards-presentation-service'],
  // [CASH_FLOW_BASE_PATH, '/cashflow-service'],
  [CATEGORIES_MANAGEMENT_BASE_PATH, '/transaction-category-collector'],
  [CONSENT_JOURNEY_CONSENT_BASE_PATH, '/consent'],
  [CONTACTS_MANAGER_JOURNEY_CONTACT_MANAGER_BASE_PATH, '/contact-manager'],
  [SELF_SERVICE_DEVICE_MANAGEMENT_V2_BASE_PATH, '/device-management-service'],
  [ENROLLMENT_MANAGER_BASE_PATH, '/enrollment-manager'],
  [MANAGE_POCKETS_JOURNEY_FINANCIAL_INSTITUTION_MANAGER_BASE_PATH, '/financial-institution-manager'],
  [INITIATE_PAYMENT_JOURNEY_PAYMENT_ORDER_BASE_PATH, '/payment-order-service'],
  [INITIATE_PAYMENT_JOURNEY_PAYMENT_ORDER_A2A_BASE_PATH, '/payment-order-a2a'],
  [PLACES_JOURNEY_BASE_PATH, '/places-presentation-service'],
  // [SAVING_GOALS_BASE_PATH, '/saving-goal-planner'],
  [STOP_CHECKS_JOURNEY_STOP_CHECKS_BASE_PATH, '/stop-checks'],
  [ACCOUNTS_TRANSACTIONS_JOURNEY_TRANSACTIONS_BASE_PATH, '/transaction-manager'],
  [USER_BASE_PATH, '/user-manager'],
  [CAMPAIGN_JOURNEY_ENGAGEMENT_BASE_PATH, '/engagement'],
  [RETAIL_LOANS_JOURNEY_LOANS_BASE_PATH, '/loan'],
  [MANAGE_POCKETS_JOURNEY_POCKET_TAILOR_BASE_PATH, '/pocket-tailor'],
  [IMPERSONATION_SERVICE_BASE_PATH, '/orchestration'],
  [IMPERSONATION_SERVICE_BASE_PATH, '/orchestration'],
  [PASSWORD_RESET_ACCOUNT_RECOVERY_MANAGER_BASE_PATH, '/account-recovery-manager'],
  [IMPERSONATION_USER_MANAGER_SERVICE_BASE_PATH, '/user-manager'],
  [REAL_TIME_COMMUNICATION_JOURNEY_BASE_PATH, '/rtc'],
];

/**
 * This module is added to the `imports` array of the AppModule in app.module.ts.
 *
 * Service configuration may be customised by modifying the relevant
 * `*_BASE_PATH` provider token value or by adding a `ModuleWithProvider`
 * as an import to this module by calling `.forRoot` on an API module:
 *
 * ```
 * @NgModule({
 *   providers: [...],
 *   imports: [
 *     AuditApiModule.forRoot(() => new AuditConfiguration({ ... }))
 *   ]
 * })
 * export class AppDataModules {}
 * ```
 */
@NgModule({
  providers: [
    ...dataModulePaths.map(([token, servicePath]) => ({
      provide: token,
      useValue: `${environment.apiRoot}${servicePath}`,
    })),
  ],
})
export class AppDataModule { }
